/**
 * Auth Error Boundary Component
 * Handles authentication system initialization failures gracefully
 */

import React, { Component, ReactNode } from 'react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  errorMessage: string | null;
  errorDetails: string | null;
}

class AuthErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      errorMessage: null,
      errorDetails: null
    };
  }

  static getDerivedStateFromError(error: Error): State {
    // Update state to show the fallback UI
    return {
      hasError: true,
      errorMessage: error.message,
      errorDetails: error.stack || null
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Log the error for debugging
    console.error('Auth Error Boundary caught an error:', error, errorInfo);
    
    // Could send error to logging service here
    // logErrorToService(error, errorInfo);
  }

  private handleRetry = () => {
    // Reset error state to retry
    this.setState({
      hasError: false,
      errorMessage: null,
      errorDetails: null
    });

    // Reload the page to reinitialize everything
    window.location.reload();
  };

  private handleReportError = () => {
    const { errorMessage, errorDetails } = this.state;
    const userAgent = navigator.userAgent;
    const timestamp = new Date().toISOString();
    
    const errorReport = {
      error: errorMessage,
      details: errorDetails,
      userAgent,
      timestamp,
      url: window.location.href
    };

    // Copy error report to clipboard
    navigator.clipboard.writeText(JSON.stringify(errorReport, null, 2))
      .then(() => {
        alert('Error details copied to clipboard. Please send this to support.');
      })
      .catch(() => {
        // Fallback: show error report in alert
        alert(`Error details:\n\n${JSON.stringify(errorReport, null, 2)}`);
      });
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default fallback UI
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
          <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-6 text-center">
            <div className="mb-4">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
                <svg 
                  className="h-6 w-6 text-red-600" 
                  fill="none" 
                  viewBox="0 0 24 24" 
                  stroke="currentColor"
                >
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth={2} 
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" 
                  />
                </svg>
              </div>
              <h2 className="text-lg font-semibold text-gray-900">
                Authentication System Error
              </h2>
              <p className="mt-2 text-sm text-gray-600">
                The authentication system failed to initialize properly. 
                This might be due to network issues or configuration problems.
              </p>
            </div>

            <div className="mb-6">
              <details className="text-left">
                <summary className="cursor-pointer text-sm text-gray-500 hover:text-gray-700">
                  Technical Details
                </summary>
                <div className="mt-2 p-3 bg-gray-100 rounded text-xs font-mono text-gray-700 break-all">
                  <div><strong>Error:</strong> {this.state.errorMessage}</div>
                  {this.state.errorDetails && (
                    <div className="mt-2">
                      <strong>Stack:</strong>
                      <pre className="whitespace-pre-wrap mt-1">
                        {this.state.errorDetails.slice(0, 500)}
                        {this.state.errorDetails.length > 500 && '...'}
                      </pre>
                    </div>
                  )}
                </div>
              </details>
            </div>

            <div className="space-y-3">
              <button
                onClick={this.handleRetry}
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Try Again
              </button>
              
              <button
                onClick={this.handleReportError}
                className="w-full flex justify-center py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Copy Error Details
              </button>
            </div>

            <div className="mt-6 pt-4 border-t border-gray-200">
              <p className="text-xs text-gray-500">
                If this problem persists, please contact support with the error details above.
              </p>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default AuthErrorBoundary;
